import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardCheck, FlaskConical, Save } from "lucide-react";

const LEVELS = [1, 2, 3, 4, 5];
const scoreColor = (s) =>
  s >= 15 ? "bg-red-100 text-red-700 border-red-300"
  : s >= 8 ? "bg-amber-100 text-amber-700 border-amber-300"
  : s >= 4 ? "bg-yellow-50 text-yellow-700 border-yellow-200"
  : "bg-lime-50 text-lime-700 border-lime-200";

export default function JobAssessment({ jobId, kind }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${jobId}/assessment/${kind}`).then((r) => {
      const data = r.data.rows || [];
      setRows(data.map((x) => kind === "risk"
        ? { id: x.id, hazard: x.hazard, likelihood: x.likelihood ?? 2, consequence: x.consequence ?? 2, mitigated: x.mitigated ?? false }
        : { id: x.id, item: x.item, checked: x.checked ?? false }));
    }).catch(() => {});
  }, [jobId, kind]);

  const set = (id, key, val) => setRows((r) => r.map((x) => (x.id === id ? { ...x, [key]: val } : x)));

  const save = async () => {
    setSaving(true);
    try { await api.put(`/jobs/${jobId}/assessment/${kind}`, { rows }); toast.success("Assessment saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (kind === "risk") {
    const maxScore = rows.reduce((m, r) => {
      const raw = r.likelihood * r.consequence;
      const s = r.mitigated ? Math.max(1, Math.round(raw / 2)) : raw;
      return Math.max(m, s);
    }, 0);
    const overall = maxScore >= 15 ? "High" : maxScore >= 8 ? "Medium" : maxScore >= 4 ? "Low-Med" : "Low";
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="job-risk-assessment">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-lime-500" /> Risk Assessment</h3>
          <span className={`text-sm font-bold px-3 py-1 rounded-lg border ${scoreColor(maxScore)}`}>{overall} · {maxScore}</span>
        </div>
        <div className="space-y-2">
          {rows.map((r) => {
            const raw = r.likelihood * r.consequence;
            const score = r.mitigated ? Math.max(1, Math.round(raw / 2)) : raw;
            return (
              <div key={r.id} className="grid grid-cols-2 sm:grid-cols-[1fr_90px_90px_80px_56px] gap-2 items-center border-b border-slate-100 pb-2">
                <span className="text-sm text-slate-700 col-span-2 sm:col-span-1">{r.hazard}</span>
                <Select value={String(r.likelihood)} onValueChange={(v) => set(r.id, "likelihood", +v)}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={String(l)}>L{l}</SelectItem>)}</SelectContent></Select>
                <Select value={String(r.consequence)} onValueChange={(v) => set(r.id, "consequence", +v)}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={String(l)}>C{l}</SelectItem>)}</SelectContent></Select>
                <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={r.mitigated} onChange={(e) => set(r.id, "mitigated", e.target.checked)} className="h-4 w-4 accent-lime-500" /> Mit.</label>
                <span className={`justify-self-end text-sm font-bold px-2 py-0.5 rounded border ${scoreColor(score)}`}>{score}</span>
              </div>
            );
          })}
        </div>
        <Button data-testid="save-risk-assessment-btn" onClick={save} disabled={saving} className="mt-4 bg-lime-400 hover:bg-lime-300 text-slate-900"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
      </div>
    );
  }

  const done = rows.filter((r) => r.checked).length;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="job-damp-assessment">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FlaskConical className="h-5 w-5 text-lime-500" /> DAMP Check</h3>
        <span className="text-sm font-medium text-slate-600">{done}/{rows.length}</span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <label key={r.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer ${r.checked ? "bg-lime-50 border-lime-300" : "bg-white border-slate-200"}`}>
            <input type="checkbox" checked={r.checked} onChange={(e) => set(r.id, "checked", e.target.checked)} className="h-5 w-5 accent-lime-500 shrink-0" />
            <span className={`text-sm ${r.checked ? "text-slate-800 font-medium" : "text-slate-600"}`}>{r.item}</span>
          </label>
        ))}
      </div>
      <Button data-testid="save-damp-assessment-btn" onClick={save} disabled={saving} className="mt-4 bg-lime-400 hover:bg-lime-300 text-slate-900"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
    </div>
  );
}
